/**
 * BudgetPacingAlertsPage — real-time budget pacing + threshold alerts report.
 *
 * Ported from apps/dash-legacy/src/routes/_authed/reports/pacing.tsx.
 * Modernized: plain Tailwind + TanStack Query; no lucide/shadcn/react-router/@/lib deps.
 * Binds to /api/v1/budget-pacing-alerts (GET list + POST compute+upsert idempotent).
 *
 * Sections:
 *  1. Header + auto-refresh indicator
 *  2. KPI tiles (total, healthy, warn, critical, avg burn%)
 *  3. Spend curve — inline SVG polyline of burn% per row, emerald stroke
 *  4. Filters (severity, scope_type, sort)
 *  5. Alert thresholds list with "Edit threshold" drawer (POST to re-upsert)
 *  6. Alert history table
 */
import { useMemo, useState } from "react";
import {
	useBudgetPacingAlertsList,
	useBudgetPacingAlertsUpsert,
} from "./hooks";
import type { BudgetPacingAlertRow, Severity } from "./api";

// ─── Style tokens (plain Tailwind, light theme) ───────────────────────────

const CARD = "rounded-lg border border-zinc-200 bg-white p-4 shadow-sm";
const CARD_MD = "rounded-md border border-zinc-200 bg-white shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500";
const SELECT = INPUT;
const BUTTON_PRIMARY =
	"inline-flex items-center justify-center rounded-md bg-gradient-to-r from-emerald-600 to-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";
const BUTTON_GHOST =
	"inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50";

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "—";
	return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function fmtPct(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "—";
	return `${Math.min(n, 9999).toFixed(1)}%`;
}

function fmtCurrency(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "—";
	return `$${n.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}

function fmtDateRange(start: number, end: number): string {
	const s = new Date(start * 1000).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
	const e = new Date(end * 1000).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
	return `${s} → ${e}`;
}

function fmtRelative(ts: number | null): string {
	if (!ts) return "—";
	const diff = Math.max(0, Math.floor(Date.now() / 1000) - ts);
	if (diff < 60) return `${diff}s ago`;
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	return `${Math.floor(diff / 86400)}d ago`;
}

function daysRemaining(period_end: number): number {
	const diff = period_end - Math.floor(Date.now() / 1000);
	return Math.max(0, Math.ceil(diff / 86400));
}

function severityTone(s: Severity): string {
	switch (s) {
		case "critical":
			return "bg-rose-50 text-rose-700 border-rose-200";
		case "warn":
			return "bg-amber-50 text-amber-700 border-amber-200";
		default:
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
	}
}

function severityLabel(s: Severity): string {
	switch (s) {
		case "critical":
			return "Critical";
		case "warn":
			return "Warning";
		default:
			return "Healthy";
	}
}

function barTone(burn: number | null): string {
	if (burn === null) return "bg-zinc-300";
	if (burn >= 95) return "bg-rose-500";
	if (burn >= 80) return "bg-amber-500";
	return "bg-emerald-500";
}

// ─── Spend curve (inline SVG polyline) ────────────────────────────────────

function SpendCurve({ rows }: { rows: BudgetPacingAlertRow[] }) {
	const W = 720;
	const H = 160;
	const PAD = 16;

	const points = useMemo(() => {
		if (!rows.length) return "";
		const values = rows.map((r) => r.burn_pct ?? 0);
		const maxV = Math.max(100, ...values);
		const step = rows.length > 1 ? (W - PAD * 2) / (rows.length - 1) : 0;
		return values
			.map((v, i) => {
				const x = PAD + i * step;
				const y = PAD + (H - PAD * 2) * (1 - v / maxV);
				return `${x.toFixed(1)},${y.toFixed(1)}`;
			})
			.join(" ");
	}, [rows]);

	if (!rows.length) {
		return (
			<p
				className="py-8 text-center text-sm text-zinc-500"
				data-testid="budget-pacing-alerts-curve-empty"
			>
				No spend curve data yet.
			</p>
		);
	}

	return (
		<svg
			viewBox={`0 0 ${W} ${H}`}
			preserveAspectRatio="none"
			className="h-40 w-full"
			role="img"
			aria-label="Budget burn curve across scopes"
			data-testid="budget-pacing-alerts-curve"
		>
			<defs>
				<linearGradient id="spend-grad" x1="0" y1="0" x2="1" y2="0">
					<stop offset="0%" stopColor="#059669" />
					<stop offset="100%" stopColor="#0284c7" />
				</linearGradient>
			</defs>
			{/* 80% warn line */}
			<line
				x1={PAD}
				x2={W - PAD}
				y1={PAD + (H - PAD * 2) * 0.2}
				y2={PAD + (H - PAD * 2) * 0.2}
				stroke="#f59e0b"
				strokeDasharray="3 3"
				strokeWidth={1}
				opacity={0.6}
			/>
			{/* 95% crit line */}
			<line
				x1={PAD}
				x2={W - PAD}
				y1={PAD + (H - PAD * 2) * 0.05}
				y2={PAD + (H - PAD * 2) * 0.05}
				stroke="#f43f5e"
				strokeDasharray="3 3"
				strokeWidth={1}
				opacity={0.6}
			/>
			<polyline
				fill="none"
				stroke="url(#spend-grad)"
				strokeWidth={2}
				strokeLinejoin="round"
				strokeLinecap="round"
				points={points}
			/>
		</svg>
	);
}

// ─── KPI tile ─────────────────────────────────────────────────────────────

function KpiTile({
	label,
	value,
	accent,
	testid,
}: {
	label: string;
	value: string;
	accent?: string;
	testid?: string;
}) {
	return (
		<div className={CARD} data-testid={testid}>
			<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
				{label}
			</p>
			<p
				className={`mt-1 text-2xl font-semibold tabular-nums ${
					accent ?? "text-zinc-900"
				}`}
			>
				{value}
			</p>
		</div>
	);
}

// ─── Threshold drawer ─────────────────────────────────────────────────────

interface DrawerState {
	open: boolean;
	row: BudgetPacingAlertRow | null;
}

function ThresholdDrawer({
	state,
	onClose,
}: {
	state: DrawerState;
	onClose: () => void;
}) {
	const [warn, setWarn] = useState<string>("");
	const [crit, setCrit] = useState<string>("");
	const [submitted, setSubmitted] = useState(false);
	const mutation = useBudgetPacingAlertsUpsert();

	// Reset form when row changes
	useMemo(() => {
		if (state.row) {
			setWarn(String(state.row.warn_threshold_pct));
			setCrit(String(state.row.crit_threshold_pct));
			setSubmitted(false);
			mutation.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.row?.id]);

	if (!state.open || !state.row) return null;
	const row = state.row;

	const warnNum = Number(warn);
	const critNum = Number(crit);
	const invalid =
		!Number.isFinite(warnNum) ||
		!Number.isFinite(critNum) ||
		warnNum < 0 ||
		warnNum > 100 ||
		critNum < 0 ||
		critNum > 100 ||
		critNum <= warnNum;

	const submit = () => {
		if (invalid) return;
		setSubmitted(true);
		mutation.mutate(
			{
				scope_type: row.scope_type,
				scope_id: row.scope_id,
				budget_cap: row.budget_cap,
				spent: row.spent,
				period_start: row.period_start,
				period_end: row.period_end,
				warn_threshold_pct: warnNum,
				crit_threshold_pct: critNum,
			},
			{
				onSuccess: () => {
					setTimeout(onClose, 400);
				},
			},
		);
	};

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end bg-zinc-900/30"
			role="dialog"
			aria-modal="true"
			aria-label="Edit pacing thresholds"
			data-testid="budget-pacing-alerts-drawer"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<aside className="flex h-full w-full max-w-md flex-col gap-5 border-l border-zinc-200 bg-white p-6 shadow-xl">
				<header className="flex items-start justify-between gap-4">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Edit thresholds
						</p>
						<h2 className="mt-1 text-lg font-semibold text-zinc-900">
							{row.scope_type} · {row.scope_id}
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className={BUTTON_GHOST}
						aria-label="Close drawer"
					>
						Close
					</button>
				</header>

				<dl className="grid grid-cols-2 gap-3 text-sm">
					<div>
						<dt className="text-xs text-zinc-500">Budget cap</dt>
						<dd className="font-medium text-zinc-900">
							{fmtCurrency(row.budget_cap)}
						</dd>
					</div>
					<div>
						<dt className="text-xs text-zinc-500">Spent</dt>
						<dd className="font-medium text-zinc-900">
							{fmtCurrency(row.spent)}
						</dd>
					</div>
					<div>
						<dt className="text-xs text-zinc-500">Burn</dt>
						<dd className="font-medium text-zinc-900">
							{fmtPct(row.burn_pct)}
						</dd>
					</div>
					<div>
						<dt className="text-xs text-zinc-500">Severity</dt>
						<dd>
							<span
								className={`inline-block rounded-md border px-2 py-0.5 text-xs font-medium ${severityTone(
									row.severity,
								)}`}
							>
								{severityLabel(row.severity)}
							</span>
						</dd>
					</div>
				</dl>

				<div className="flex flex-col gap-4">
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
							Warn threshold (%)
						</span>
						<input
							type="number"
							min={0}
							max={100}
							step={1}
							value={warn}
							onChange={(e) => setWarn(e.target.value)}
							className={INPUT}
							data-testid="budget-pacing-alerts-warn-input"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
							Critical threshold (%)
						</span>
						<input
							type="number"
							min={0}
							max={100}
							step={1}
							value={crit}
							onChange={(e) => setCrit(e.target.value)}
							className={INPUT}
							data-testid="budget-pacing-alerts-crit-input"
						/>
					</label>

					{invalid && submitted && (
						<p className="text-xs text-rose-600" role="alert">
							Critical must exceed warn; both must be 0–100.
						</p>
					)}
					{mutation.isError && (
						<p className="text-xs text-rose-600" role="alert">
							{mutation.error?.message ?? "Failed to save"}
						</p>
					)}
					{mutation.isSuccess && (
						<p className="text-xs text-emerald-700">Saved.</p>
					)}
				</div>

				<footer className="mt-auto flex items-center justify-end gap-2">
					<button
						type="button"
						className={BUTTON_GHOST}
						onClick={onClose}
						disabled={mutation.isPending}
					>
						Cancel
					</button>
					<button
						type="button"
						className={BUTTON_PRIMARY}
						onClick={submit}
						disabled={invalid || mutation.isPending}
						data-testid="budget-pacing-alerts-save"
					>
						{mutation.isPending ? "Saving…" : "Save thresholds"}
					</button>
				</footer>
			</aside>
		</div>
	);
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function BudgetPacingAlertsPage() {
	const [severity, setSeverity] = useState<Severity | "">("");
	const [scopeType, setScopeType] = useState<string>("");
	const [sort, setSort] = useState<"burn" | "created_at">("burn");
	const [drawer, setDrawer] = useState<DrawerState>({
		open: false,
		row: null,
	});

	const listQuery = useBudgetPacingAlertsList({
		severity: severity || undefined,
		scope_type: scopeType || undefined,
		sort,
	});

	const items = listQuery.data?.items ?? [];

	const summary = useMemo(() => {
		const total = items.length;
		const healthy = items.filter((r) => r.severity === "none").length;
		const warn = items.filter((r) => r.severity === "warn").length;
		const critical = items.filter((r) => r.severity === "critical").length;
		const burns = items
			.map((r) => r.burn_pct)
			.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
		const avgBurn =
			burns.length > 0 ? burns.reduce((a, b) => a + b, 0) / burns.length : null;
		const totalCap = items.reduce((a, r) => a + (r.budget_cap || 0), 0);
		const totalSpent = items.reduce((a, r) => a + (r.spent || 0), 0);
		const usedPct = totalCap > 0 ? (totalSpent / totalCap) * 100 : null;
		return { total, healthy, warn, critical, avgBurn, totalCap, totalSpent, usedPct };
	}, [items]);

	// Sort by burn for curve visual regardless of table order
	const curveRows = useMemo(() => {
		return [...items].sort(
			(a, b) => (b.burn_pct ?? -1) - (a.burn_pct ?? -1),
		);
	}, [items]);

	return (
		<main
			data-testid="budget-pacing-alerts-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8"
		>
			{/* Header */}
			<header className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Report
					</p>
					<h1 className="mt-1 bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Budget Pacing Alerts
					</h1>
					<p className="mt-1 text-sm text-zinc-500">
						Real-time budget burn + pacing alerts across offers, campaigns,
						advertisers, and publishers.
					</p>
				</div>
				<div className="flex items-center gap-2 text-xs text-zinc-500">
					<span
						className={`inline-flex h-2 w-2 rounded-full ${
							listQuery.isFetching
								? "animate-pulse bg-emerald-500"
								: "bg-emerald-400"
						}`}
						aria-hidden
					/>
					<span>
						{listQuery.isFetching ? "Refreshing…" : "Live"} · {items.length}{" "}
						alert{items.length === 1 ? "" : "s"}
					</span>
					<button
						type="button"
						className={BUTTON_GHOST}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="budget-pacing-alerts-refresh"
					>
						Refresh
					</button>
				</div>
			</header>

			{/* KPI tiles */}
			<section
				className="grid grid-cols-2 gap-3 md:grid-cols-5"
				aria-label="Summary tiles"
				data-testid="budget-pacing-alerts-kpis"
			>
				<KpiTile
					label="Total alerts"
					value={fmtNum(summary.total)}
					testid="budget-pacing-alerts-kpi-total"
				/>
				<KpiTile
					label="Healthy"
					value={fmtNum(summary.healthy)}
					accent="text-emerald-700"
				/>
				<KpiTile
					label="Warning"
					value={fmtNum(summary.warn)}
					accent="text-amber-700"
				/>
				<KpiTile
					label="Critical"
					value={fmtNum(summary.critical)}
					accent="text-rose-700"
				/>
				<KpiTile
					label="Avg burn"
					value={fmtPct(summary.avgBurn)}
					accent="text-sky-700"
				/>
			</section>

			{/* Budget totals + spend curve */}
			<section
				className={CARD_MD}
				aria-label="Aggregate budget usage + spend curve"
			>
				<div className="flex flex-wrap items-end justify-between gap-4 border-b border-zinc-100 px-5 py-4">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Aggregate spend
						</p>
						<p className="mt-1 text-xl font-semibold text-zinc-900 tabular-nums">
							{fmtCurrency(summary.totalSpent)}{" "}
							<span className="text-sm font-normal text-zinc-500">
								of {fmtCurrency(summary.totalCap)}
							</span>
						</p>
					</div>
					<div className="text-right">
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Used
						</p>
						<p className="mt-1 text-xl font-semibold text-sky-700 tabular-nums">
							{fmtPct(summary.usedPct)}
						</p>
					</div>
				</div>
				<div className="px-5 py-4">
					<SpendCurve rows={curveRows} />
					<p className="mt-2 text-xs text-zinc-500">
						Burn % per scope, sorted high→low. Amber = 80% warn, rose = 95% crit.
					</p>
				</div>
			</section>

			{/* Filters */}
			<section
				className="flex flex-wrap items-center gap-3 rounded-md border border-zinc-200 bg-white p-3 shadow-sm"
				aria-label="Filters"
			>
				<label className="flex items-center gap-2 text-xs text-zinc-600">
					<span>Severity</span>
					<select
						className={SELECT}
						style={{ width: 140 }}
						value={severity}
						onChange={(e) => setSeverity(e.target.value as Severity | "")}
						data-testid="budget-pacing-alerts-severity-filter"
					>
						<option value="">All</option>
						<option value="none">Healthy</option>
						<option value="warn">Warning</option>
						<option value="critical">Critical</option>
					</select>
				</label>
				<label className="flex items-center gap-2 text-xs text-zinc-600">
					<span>Scope</span>
					<select
						className={SELECT}
						style={{ width: 140 }}
						value={scopeType}
						onChange={(e) => setScopeType(e.target.value)}
						data-testid="budget-pacing-alerts-scope-filter"
					>
						<option value="">All</option>
						<option value="offer">Offer</option>
						<option value="campaign">Campaign</option>
						<option value="advertiser">Advertiser</option>
						<option value="publisher">Publisher</option>
					</select>
				</label>
				<label className="flex items-center gap-2 text-xs text-zinc-600">
					<span>Sort</span>
					<select
						className={SELECT}
						style={{ width: 140 }}
						value={sort}
						onChange={(e) =>
							setSort(e.target.value as "burn" | "created_at")
						}
						data-testid="budget-pacing-alerts-sort"
					>
						<option value="burn">By burn %</option>
						<option value="created_at">Newest first</option>
					</select>
				</label>
			</section>

			{/* Threshold list + history table */}
			<section className={CARD_MD} aria-label="Alert history">
				<div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3">
					<h2 className="text-sm font-semibold text-zinc-900">
						Alert history
					</h2>
					<p className="text-xs text-zinc-500">
						Click a row to edit its thresholds.
					</p>
				</div>

				{listQuery.isLoading ? (
					<p
						className="px-5 py-6 text-sm text-zinc-500"
						data-testid="budget-pacing-alerts-loading"
					>
						Loading pacing alerts…
					</p>
				) : listQuery.error ? (
					<p
						className="px-5 py-6 text-sm text-rose-600"
						role="alert"
						data-testid="budget-pacing-alerts-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<div
						className="px-5 py-10 text-center"
						data-testid="budget-pacing-alerts-empty"
					>
						<p className="text-sm text-zinc-500">
							No pacing alerts yet. Alerts appear as budgets are tracked.
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="budget-pacing-alerts-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Scope</th>
									<th className={TH}>Period</th>
									<th className={`${TH} text-right`}>Spent / Cap</th>
									<th className={TH}>Burn</th>
									<th className={`${TH} text-right`}>Pace</th>
									<th className={`${TH} text-right`}>Projected</th>
									<th className={`${TH} text-right`}>Days left</th>
									<th className={TH}>Severity</th>
									<th className={TH}>Alerted</th>
									<th className={TH}>Thresholds</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((r) => {
									const burn = r.burn_pct ?? 0;
									const barPct = Math.min(burn, 100);
									return (
										<tr
											key={r.id}
											data-testid={`budget-pacing-alerts-row-${r.id}`}
											className="cursor-pointer hover:bg-zinc-50/60"
											onClick={() =>
												setDrawer({ open: true, row: r })
											}
										>
											<td
												className={`${TD} max-w-[180px] truncate font-medium`}
											>
												<span className="inline-block rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
													{r.scope_type}
												</span>{" "}
												{r.scope_id}
											</td>
											<td className={`${TD} text-xs text-zinc-500`}>
												{fmtDateRange(r.period_start, r.period_end)}
											</td>
											<td className={`${TD} text-right tabular-nums`}>
												{fmtCurrency(r.spent)} /{" "}
												<span className="text-zinc-500">
													{fmtCurrency(r.budget_cap)}
												</span>
											</td>
											<td className={TD}>
												<div className="flex items-center gap-2">
													<div className="h-2 w-28 overflow-hidden rounded-full bg-zinc-100">
														<div
															className={`h-full rounded-full transition-all ${barTone(
																r.burn_pct,
															)}`}
															style={{ width: `${barPct}%` }}
														/>
													</div>
													<span className="w-14 text-right text-xs font-medium tabular-nums text-zinc-700">
														{fmtPct(r.burn_pct)}
													</span>
												</div>
											</td>
											<td
												className={`${TD} text-right tabular-nums text-zinc-700`}
											>
												{r.pace_ratio === null
													? "—"
													: `${r.pace_ratio.toFixed(2)}×`}
											</td>
											<td
												className={`${TD} text-right tabular-nums text-zinc-700`}
											>
												{fmtCurrency(r.projected_end_spend)}
											</td>
											<td
												className={`${TD} text-right tabular-nums text-zinc-600`}
											>
												{daysRemaining(r.period_end)}d
											</td>
											<td className={TD}>
												<span
													className={`inline-block rounded-md border px-2 py-0.5 text-xs font-medium ${severityTone(
														r.severity,
													)}`}
												>
													{severityLabel(r.severity)}
												</span>
											</td>
											<td className={`${TD} text-xs text-zinc-500`}>
												{fmtRelative(r.alerted_at)}
											</td>
											<td className={`${TD} text-xs text-zinc-600`}>
												<span className="text-amber-700">
													{r.warn_threshold_pct}%
												</span>{" "}
												/{" "}
												<span className="text-rose-700">
													{r.crit_threshold_pct}%
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<ThresholdDrawer
				state={drawer}
				onClose={() => setDrawer({ open: false, row: null })}
			/>
		</main>
	);
}

export default BudgetPacingAlertsPage;
